import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";

interface Props {
  markdown: string;
  className?: string;
}

/**
 * Markdown renderer for dossier sections. Tailwind v4 doesn't ship `@tailwindcss/typography`
 * so we hand-style headings, lists, tables, code blocks etc. with inline classNames.
 */
export function Markdown({ markdown, className }: Props) {
  return (
    <div className={cn("space-y-4 text-sm leading-relaxed", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (props) => (
            <h1 className="text-2xl font-semibold tracking-tight" {...props} />
          ),
          h2: (props) => (
            <h2 className="mt-6 text-xl font-semibold tracking-tight" {...props} />
          ),
          h3: (props) => (
            <h3 className="mt-4 text-lg font-medium" {...props} />
          ),
          h4: (props) => <h4 className="mt-4 font-medium" {...props} />,
          p: (props) => <p className="leading-7" {...props} />,
          ul: (props) => <ul className="list-disc pl-6 space-y-1" {...props} />,
          ol: (props) => (
            <ol className="list-decimal pl-6 space-y-1" {...props} />
          ),
          li: (props) => <li {...props} />,
          a: (props) => (
            <a
              className="text-primary underline underline-offset-2"
              target={props.href?.startsWith("http") ? "_blank" : undefined}
              rel={props.href?.startsWith("http") ? "noreferrer" : undefined}
              {...props}
            />
          ),
          blockquote: (props) => (
            <blockquote
              className="border-l-2 border-muted-foreground/30 pl-3 italic text-muted-foreground"
              {...props}
            />
          ),
          table: (props) => (
            <div className="my-2 overflow-x-auto">
              <table className="w-full border-collapse text-sm" {...props} />
            </div>
          ),
          thead: (props) => <thead className="bg-muted/40" {...props} />,
          th: (props) => (
            <th
              className="border-b px-3 py-1.5 text-left font-medium"
              {...props}
            />
          ),
          td: (props) => (
            <td className="border-b px-3 py-1.5 align-top" {...props} />
          ),
          code: (props) => {
            // Distinguish inline code from fenced blocks
            const { children, className: cls, ...rest } = props as {
              children?: React.ReactNode;
              className?: string;
            };
            const isBlock = (cls ?? "").startsWith("language-");
            if (isBlock) {
              return (
                <code className={cn("text-xs", cls)} {...rest}>
                  {children}
                </code>
              );
            }
            return (
              <code
                className="rounded bg-muted px-1 py-0.5 font-mono text-xs"
                {...rest}
              >
                {children}
              </code>
            );
          },
          pre: (props) => (
            <pre
              className="overflow-x-auto rounded-md border bg-muted/30 p-3 font-mono text-xs"
              {...props}
            />
          ),
          hr: () => <hr className="my-4 border-muted-foreground/20" />,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
